import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import {
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    IconButton,
    InputAdornment,
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
} from "@mui/material";
import { Visibility, VisibilityOff, ArrowBack } from "@mui/icons-material";
import { CCard, CCardHeader } from "@coreui/react";

const RegisterPartner = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone_number: "",
        username: "",
        password: "",
        state: "",
        city: "",
        address: "",
        user_type: "",
        image: null,
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    useEffect(() => {
        if (!localStorage.getItem("status")) {
            navigate("/");
        }
        fetchStates();
    }, [navigate]);

    const fetchStates = async () => {
        try {
            const response = await axios.get("https://dawabajar.encantotech.in/api/get_states");
            if (response.data.success) {
                setStates(response.data.data);
            } else {
                Swal.fire("Error", "Failed to fetch states", "error");
            }
        } catch (error) {
            Swal.fire("Error", "Error fetching states", "error");
        }
    };

    const fetchCities = async (stateId) => {
        setLoadingCities(true);
        try {
            const response = await axios.get(`https://dawabajar.encantotech.in/api/get_cities/${stateId}`);
            if (response.data.success) {
                setCities(response.data.data);
            } else {
                Swal.fire("Error", "Failed to fetch cities", "error");
            }
        } catch (error) {
            Swal.fire("Error", "Error fetching cities", "error");
        } finally {
            setLoadingCities(false);
        }
    };

    const validateField = (name, value) => {
        let error = "";
        if (name === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            error = "Invalid email format";
        } else if (name === "phone_number" && value && !/^\d{10}$/.test(value)) {
            error = "Phone number must be 10 digits";
        } else if (name === "password" && value && value.length < 6) {
            error = "Password must be at least 6 characters";
        } else if (!value && name !== "address" && name !== "image") {
            error = "This field is required";
        }
        return error;
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        const newValue = files ? files[0] : value;
        setFormData((prev) => ({
            ...prev,
            [name]: newValue,
        }));
        setErrors((prev) => ({
            ...prev,
            [name]: validateField(name, newValue),
        }));
        if (name === "image" && files[0]) {
            setPreviewImage(URL.createObjectURL(files[0]));
        } else if (name === "image" && !files[0]) {
            setPreviewImage(null);
        }
        if (name === "state") {
            setFormData((prev) => ({ ...prev, city: "" }));
            setCities([]);
            if (value) fetchCities(value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};
        Object.keys(formData).forEach((key) => {
            if (key !== "address" && key !== "image") {
                newErrors[key] = validateField(key, formData[key]);
            }
        });
        setErrors(newErrors);
        if (Object.values(newErrors).some((error) => error)) {
            Swal.fire("Warning", "Please fix the errors in the form", "warning");
            return;
        }
        setLoading(true);
        const data = new FormData();
        Object.keys(formData).forEach((key) => {
            if (formData[key] !== null) data.append(key, formData[key]);
        });

        try {
            const response = await axios.post(
                "https://dawabajar.encantotech.in/api/register_partner",
                data,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            if (response.data.status === 200) {
                Swal.fire("Success", response.data.message || "Partner registered successfully", "success").then(() => {
                    navigate("/all-partners");
                });
            } else {
                Swal.fire("Error", response.data.message || "Failed to register partner", "error");
            }
        } catch (error) {
            Swal.fire("Error", error.response?.data?.message || "Error registering partner", "error");
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-sm-2 p-0">
                    <Sidebar />
                </div>
                <div className="col-sm-10">
                    <div className="container-fluid">
                        <Navbar />
                        <CCard className="mb-4 ml-3 mt-2">
                            <CCardHeader sx={{ backgroundColor: "#16035e", color: "#fff" }}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ArrowBack />}
                                        onClick={() => navigate("/all-partners")}
                                        sx={{ borderColor: "#16035e", color: "#16035e", "&:hover": { borderColor: "#16035e" } }}
                                    >
                                        Back
                                    </Button>
                                    <Typography variant="h6" component="div">
                                        Register Partner
                                    </Typography>
                                </Box>
                            </CCardHeader>
                            <Card sx={{ m: 3, boxShadow: 3, borderRadius: 2 }}>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom color="#16035e" sx={{ fontWeight: "bold" }}>
                                        Partner Registration Form
                                    </Typography>
                                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    label="Name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    error={!!errors.name}
                                                    helperText={errors.name}
                                                    required

                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    label="Email"
                                                    name="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    error={!!errors.email}
                                                    helperText={errors.email}
                                                    required

                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    label="Phone Number"
                                                    name="phone_number"
                                                    value={formData.phone_number}
                                                    onChange={handleChange}
                                                    error={!!errors.phone_number}
                                                    helperText={errors.phone_number}
                                                    required

                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    label="Username"
                                                    name="username"
                                                    value={formData.username}
                                                    onChange={handleChange}
                                                    error={!!errors.username}
                                                    helperText={errors.username}
                                                    required

                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    label="Password"
                                                    name="password"
                                                    type={showPassword ? "text" : "password"}
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    error={!!errors.password}
                                                    helperText={errors.password}
                                                    required
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton onClick={togglePasswordVisibility}>
                                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}

                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth error={!!errors.user_type} required>
                                                    <InputLabel>User Type</InputLabel>
                                                    <Select
                                                        name="user_type"
                                                        value={formData.user_type}
                                                        onChange={handleChange}
                                                        label="User Type"
                                                        sx={{ borderRadius: 1 }}
                                                    >
                                                        <MenuItem value=""><em>Select User Type</em></MenuItem>
                                                        <MenuItem value="delivery">Delivery</MenuItem>
                                                        <MenuItem value="sales">Sales</MenuItem>
                                                    </Select>
                                                    {errors.user_type && (
                                                        <Typography color="error" variant="caption">
                                                            {errors.user_type}
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth error={!!errors.state} required>
                                                    <InputLabel>State</InputLabel>
                                                    <Select
                                                        name="state"
                                                        value={formData.state}
                                                        onChange={handleChange}
                                                        label="State"
                                                        sx={{ borderRadius: 1 }}
                                                    >
                                                        <MenuItem value=""><em>Select State</em></MenuItem>
                                                        {states.map((state) => (
                                                            <MenuItem key={state.id} value={state.id}>
                                                                {state.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {errors.state && (
                                                        <Typography color="error" variant="caption">
                                                            {errors.state}
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth error={!!errors.city} required>
                                                    <InputLabel>City</InputLabel>
                                                    <Select
                                                        name="city"
                                                        value={formData.city}
                                                        onChange={handleChange}
                                                        label="City"
                                                        disabled={!formData.state || loadingCities}
                                                        sx={{ borderRadius: 1 }}
                                                    >
                                                        <MenuItem value=""><em>Select City</em></MenuItem>
                                                        {loadingCities ? (
                                                            <MenuItem disabled>Loading...</MenuItem>
                                                        ) : (
                                                            cities.map((city) => (
                                                                <MenuItem key={city.id} value={city.id}>
                                                                    {city.name}
                                                                </MenuItem>
                                                            ))
                                                        )}
                                                    </Select>
                                                    {errors.city && (
                                                        <Typography color="error" variant="caption">
                                                            {errors.city}
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    label="Address"
                                                    name="address"
                                                    value={formData.address}
                                                    onChange={handleChange}
                                                    multiline
                                                    rows={3}

                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    type="file"
                                                    name="image"
                                                    onChange={handleChange}
                                                    InputLabelProps={{ shrink: true }}
                                                    label="Upload Image"

                                                />
                                                {previewImage && (
                                                    <Box sx={{ mt: 2, textAlign: "center" }}>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Image Preview
                                                        </Typography>
                                                        <img
                                                            src={previewImage}
                                                            alt="Preview"
                                                            style={{
                                                                maxWidth: "200px",
                                                                maxHeight: "200px",
                                                                border: "1px solid #16035e",
                                                                borderRadius: "8px",
                                                                marginTop: "8px",
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    sx={{
                                                        mt: 2,
                                                        backgroundColor: "#16035e",
                                                        py: 1.5,
                                                        px: 4,
                                                        borderRadius: 1,
                                                        "&:hover": { backgroundColor: "#1a046f" },
                                                    }}
                                                    disabled={loading}
                                                >
                                                    {loading ? <CircularProgress size={24} color="inherit" /> : "Register Partner"}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </CardContent>
                            </Card>
                        </CCard>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterPartner;